import Big from 'big.js';

export const DISCOUNT_TYPES = Object.freeze({
  NONE: 'NONE',
  PERCENTAGE: 'PERCENTAGE',
  FIXED_AMOUNT: 'FIXED_AMOUNT'
});

const SUPPORTED_DISCOUNT_TYPES = new Set(Object.values(DISCOUNT_TYPES));
const HALF_UP = 1;

const isEmptyValue = value =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const normalizeDiscountType = type => {
  if (isEmptyValue(type)) return DISCOUNT_TYPES.NONE;
  return typeof type === 'string' ? type.trim().toUpperCase() : type;
};

const normalizeDiscountValue = (type, value) => {
  if (isEmptyValue(value)) return type === DISCOUNT_TYPES.NONE ? 0 : Number.NaN;
  return Number(value);
};

const getDiscountValueForCalculation = (discount, type) => {
  if (discount === undefined || discount === null) return 0;
  if (isEmptyValue(discount.value) && type === DISCOUNT_TYPES.NONE) return 0;
  return discount.value;
};

const parseDecimal = value => {
  if (isEmptyValue(value)) return null;

  try {
    return new Big(String(value).trim());
  } catch {
    return null;
  }
};

const roundHalfUpToNumber = value => Number(value.round(2, HALF_UP).toFixed(2));

export function normalizeDiscount(discount) {
  if (discount === undefined || discount === null) {
    return {
      type: DISCOUNT_TYPES.NONE,
      value: 0
    };
  }

  const type = normalizeDiscountType(discount.type);

  return {
    type,
    value: normalizeDiscountValue(type, discount.value)
  };
}

export function validateDiscount(amount, discount) {
  const errors = [];
  const normalizedDiscount = normalizeDiscount(discount);
  const discountValue = getDiscountValueForCalculation(discount, normalizedDiscount.type);
  const amountDecimal = parseDecimal(amount);

  if (!amountDecimal || amountDecimal.lt(0)) {
    errors.push('Cena bazowa musi być poprawną liczbą większą lub równą 0.');
  }

  if (!SUPPORTED_DISCOUNT_TYPES.has(normalizedDiscount.type)) {
    errors.push('Nieobsługiwany typ rabatu.');
    return {
      valid: false,
      errors
    };
  }

  const valueDecimal = parseDecimal(discountValue);
  if (!valueDecimal) {
    errors.push('Wartość rabatu musi być poprawną liczbą.');
    return {
      valid: false,
      errors
    };
  }

  if (normalizedDiscount.type === DISCOUNT_TYPES.NONE && !valueDecimal.eq(0)) {
    errors.push('Dla braku rabatu wartość musi wynosić 0.');
  }

  if (normalizedDiscount.type === DISCOUNT_TYPES.PERCENTAGE &&
    (valueDecimal.lt(0) || valueDecimal.gt(100))) {
    errors.push('Rabat procentowy musi mieścić się w zakresie od 0 do 100.');
  }

  if (normalizedDiscount.type === DISCOUNT_TYPES.FIXED_AMOUNT && amountDecimal &&
    (valueDecimal.lt(0) || valueDecimal.gt(amountDecimal))) {
    errors.push('Rabat kwotowy musi mieścić się w zakresie od 0 do ceny bazowej.');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export function calculateAmountAfterDiscount(amount, discount) {
  const normalizedDiscount = normalizeDiscount(discount);
  const discountValue = getDiscountValueForCalculation(discount, normalizedDiscount.type);
  const calculationDiscount = {
    type: normalizedDiscount.type,
    value: discountValue
  };
  const validation = validateDiscount(amount, calculationDiscount);

  if (!validation.valid) throw new RangeError(validation.errors[0]);

  const amountDecimal = parseDecimal(amount);
  const discountDecimal = parseDecimal(discountValue);
  let amountAfterDiscount;

  if (normalizedDiscount.type === DISCOUNT_TYPES.PERCENTAGE) {
    amountAfterDiscount = amountDecimal.times(new Big(100).minus(discountDecimal)).div(100);
  } else if (normalizedDiscount.type === DISCOUNT_TYPES.FIXED_AMOUNT) {
    amountAfterDiscount = amountDecimal.minus(discountDecimal);
  } else {
    amountAfterDiscount = amountDecimal;
  }

  return roundHalfUpToNumber(amountAfterDiscount);
}

export function calculatePaymentTotal(amount, tip) {
  const amountDecimal = parseDecimal(amount);
  const tipDecimal = parseDecimal(tip);

  if (!amountDecimal || amountDecimal.lt(0)) {
    throw new RangeError('Kwota po rabacie musi być poprawną liczbą większą lub równą 0.');
  }

  if (!tipDecimal || tipDecimal.lt(0)) {
    throw new RangeError('Napiwek musi być poprawną liczbą większą lub równą 0.');
  }

  return roundHalfUpToNumber(amountDecimal.plus(tipDecimal));
}

export function getDiscountForRequest(saleType, discount) {
  if (saleType !== 'SERVICE') return undefined;
  return normalizeDiscount(discount);
}

export function buildSaleRequest(saleData) {
  const request = { ...(saleData ?? {}) };
  let discount;

  if (request.type === 'PRODUCT') {
    const productId = Number(request.productId);
    if (!Number.isInteger(productId) || productId <= 0) {
      throw new RangeError('Wybierz produkt.');
    }
    request.productId = productId;
    delete request.name;
  } else {
    delete request.productId;
  }

  if (request.type === 'SERVICE') {
    const validation = validateDiscount(request.amount, request.discount);
    if (!validation.valid) throw new RangeError(validation.errors.join(' '));

    discount = getDiscountForRequest(request.type, request.discount);
    const sourceValue = getDiscountValueForCalculation(request.discount, discount.type);
    const sourceDecimal = parseDecimal(sourceValue);
    const requestDecimal = parseDecimal(discount.value);

    if (!sourceDecimal || !requestDecimal || !sourceDecimal.eq(requestDecimal)) {
      throw new RangeError('Wartość rabatu ma zbyt dużą precyzję.');
    }
  }

  delete request.amountAfterDiscount;
  delete request.voucherStockTracked;
  delete request.saleType;
  delete request.discount;

  if (discount !== undefined) {
    request.discount = discount;
  }

  return request;
}

export function getSaleDisplayAmount(saleOrTransaction) {
  return saleOrTransaction?.amountAfterDiscount ?? saleOrTransaction?.amount;
}

export function getCustomerPaymentAmount(saleOrTransaction) {
  const saleType = saleOrTransaction?.type ?? saleOrTransaction?.saleType;
  const amount = saleType === 'SERVICE'
    ? getSaleDisplayAmount(saleOrTransaction)
    : saleOrTransaction?.amount;

  return calculatePaymentTotal(amount, saleOrTransaction?.tip ?? 0);
}
